import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Grid, IconButton, Link, Paper, Tooltip, Box, Button } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { AiFillFileText } from 'react-icons/ai';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { MdOutlineInfo } from 'react-icons/md';
import { ClientDTO, IFile, ITask, IUserDTO, NoteDTO, getClientById } from 'wisestep-api';
import {
  WsColors,
  WsDialog,
  WsIconMoreVer,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  activityFormattedDate,
  timeSince,
} from 'wisestep-base';

import { notesAmplitudeEvents } from '../../..//features/clientDetails/components/notes/helper/notesHelper';
import { NotesParent } from '../../../components/communicationBar/Notes';
import { useUserContext } from '../../../context/UserContext/UserContext';
import { useWorkspaceContext } from '../../../context/WorkspaceContext/WorkspaceContext';
import useAdjustHeightOnZoom from '../../../features/candidateLayout/hooks/useAdjustHeightOnZoom';
import { SmallBoxWithRadius } from '../../../features/candidates/utils/CandidateHelper';
import CandidateTags from '../../../features/clientDetails/components/CandidateTags';
import AddEditNewNote from '../../../features/clientDetails/components/notes/AddEditNewNote';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import { downloadCV } from '../../../features/conversationWidget/utils/cvHelper';
import { closeTask } from '../../../features/tasks/utils/TaskHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { NotesBoxWithRadius } from '../helper/NotesHelper';

import UnmarkedMissingDetails from './UnmarkedMissingDetails';

interface INotesProps {
  note: NoteDTO;
  parentId: string;
  isAdding?: boolean;
  saveNote: SubmitHandler<FieldValues>;
  editNote: () => void;
  isEditing: boolean;
  onCancel: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  parent: NotesParent;
  task?: ITask;
  noteSelectType?: string;
}

const NotesRightPanel = ({
  note,
  isAdding,
  saveNote,
  editNote,
  isEditing,
  onCancel,
  onArchive,
  onUnarchive,
  parent,
  task,
  noteSelectType,
}: INotesProps) => {
  const zoomLevel = useAdjustHeightOnZoom();
  const methods = useFormContext();
  const [parentInfo, setParentInfo] = useState<ClientDTO>();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspaceContext = useWorkspaceContext();
  const owns = workspaceContext.owns;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (note.client?.parentId) {
        const response = await getClientById(note.client?.parentId as string);
        setParentInfo(response.data.data);
      }
    };
    fetchData();
  }, [note.client?.parentId]);

  const handleNavigation = (id, name, notePath) => {
    switch (notePath) {
      case NotesParent.CLIENT:
        return `/client-details?tabIndex=0&isProfile=true&isChild=${
          note.client.parentId != null ? false : true
        }&name=${parentInfo?.name}&clientId=${id}`;
      case NotesParent.CLIENTCONTACT:
        return `/${id}/client-contact-details?isChild=true&isProfile=true&name=${name}&breadCrumbList=${getEncodedQueryParams(
          ClintPageBreadCrumb['CLIENTS']
        )}`;
      default:
        return `/client-details?tabIndex=0&isProfile=true&isChild=false&name=${name}&clientId${id}&breadCrumbList=${getEncodedQueryParams(
          ClintPageBreadCrumb['CLIENTS']
        )}`;
    }
  };

  const handleDownloadCV = (file: IFile) => {
    notesAmplitudeEvents('note_attachment_download', 'Attachment downloaded', workspaceContext);
    downloadCV(file.location.split('?')[0], file.name);
  };

  const onCompleteTask = async (taskId: string) => setNotification(await closeTask(taskId));

  const handleClose = () => {
    setOpenDialog(false);
    onCancel();
  };

  return (
    <>
      {isAdding || isEditing ? (
        <FormProvider {...methods}>
          <AddEditNewNote onSubmit={saveNote} onCancel={onCancel} note={note} isAdding={isAdding} />
        </FormProvider>
      ) : (
        <Paper
          sx={{
            borderColor: WsColors.TertiaryShades100,
            background: WsColors.White,
            maxHeight: `${(1 + (1 - zoomLevel)) * 80}vh`,
            overflowY: 'auto',
            minHeight: 590,
          }}
        >
          <Grid container direction="column" px={2} py={1} display="flex">
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item container spacing={2} xs>
                {note.noteType && (
                  <Grid item>
                    <SmallBoxWithRadius sx={{ px: 1 }}>
                      <WsTypography
                        color={WsTypoColors.PRIMARY600}
                        variant={WsTypoVariants.BODY1}
                        hoverText="Note Type"
                        cursor="default"
                      >
                        <Grid container alignItems="center">
                          <DescriptionOutlinedIcon
                            sx={{
                              color: WsColors.TheBlues600,
                              marginRight: 0.5,
                              fontSize: 17,
                            }}
                          />
                          {note.noteType}
                        </Grid>
                      </WsTypography>
                    </SmallBoxWithRadius>
                  </Grid>
                )}
                {note.client && (
                  <Grid item>
                    <SmallBoxWithRadius sx={{ px: 1 }}>
                      <Link
                        underline="none"
                        href={handleNavigation(
                          note.client.id,
                          note.client.name,
                          NotesParent.CLIENT
                        )}
                      >
                        <WsTypography
                          color={WsTypoColors.PRIMARY600}
                          variant={WsTypoVariants.BODY1}
                          hoverText={note.client.parentId != null ? 'Child Client' : 'Client'}
                          cursor="pointer"
                        >
                          <Grid container alignItems="center">
                            <HiOutlineOfficeBuilding
                              size={17}
                              color={WsColors.TheBlues600}
                              style={{ marginRight: 5 }}
                            />
                            {note.client.name}
                          </Grid>
                        </WsTypography>
                      </Link>
                    </SmallBoxWithRadius>
                  </Grid>
                )}
                {note?.clientContact && (
                  <Grid item>
                    <SmallBoxWithRadius sx={{ px: 1 }}>
                      <Link
                        underline="none"
                        href={handleNavigation(
                          note.clientContact.id,
                          note.client.name,
                          NotesParent.CLIENTCONTACT
                        )}
                      >
                        <WsTypography
                          color={WsTypoColors.PRIMARY}
                          variant={WsTypoVariants.BODY1}
                          hoverText="Contact"
                          cursor="pointer"
                        >
                          <Grid container alignItems="center">
                            <PersonOutlineIcon sx={{ marginRight: 0.5, fontSize: 19 }} />
                            {note.clientContact?.firstName}
                          </Grid>
                        </WsTypography>
                      </Link>
                    </SmallBoxWithRadius>
                  </Grid>
                )}
              </Grid>
              {(task?.dueDate || task?.modifiedOn) && (
                <Grid item>
                  <NotesBoxWithRadius status={task?.status ?? ''}>
                    <WsTypography
                      color={WsTypoColors.BLACK}
                      variant={WsTypoVariants.SUB_TITLE}
                      hoverText={
                        task?.status === 'OPEN' ? 'Reminder due date' : 'Reminder complete'
                      }
                      cursor="default"
                    >
                      <Grid container alignItems="center">
                        <NotificationsNoneOutlinedIcon
                          sx={{
                            color:
                              task?.status === 'OPEN'
                                ? WsColors.Yellow500
                                : WsColors.GenericRichBlack,
                            marginRight: 0.5,
                            fontSize: 25,
                          }}
                        />
                        {activityFormattedDate(
                          (task?.dueDate ?? task?.modifiedOn)?.toString() ?? ''
                        )}
                      </Grid>
                    </WsTypography>
                  </NotesBoxWithRadius>
                </Grid>
              )}

              {task?.status === 'OPEN' && (
                <Grid item>
                  <Tooltip title="Mark as Complete">
                    <Button
                      startIcon={
                        <DoneOutlinedIcon
                          sx={{
                            fontSize: 40,
                          }}
                        />
                      }
                      onClick={() => onCompleteTask(note?.taskId ?? '')}
                    />
                  </Tooltip>
                </Grid>
              )}
              {note?.createdByName && (
                <Grid item>
                  <Tooltip
                    title={`Added by ${note?.createdByName}, ${timeSince(
                      String(note?.createdOn)
                    )} `}
                  >
                    <IconButton onClick={() => {}}>
                      <MdOutlineInfo size={20} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <Grid item mt={0.5}>
                {parent != NotesParent.NOTELISTING &&
                  (owns || loggedInUser.email === note.createdBy) && (
                    <PopoverOnClick element={<WsIconMoreVer />}>
                      <Box p={1}>
                        <WsTypography
                          variant={WsTypoVariants.SUB_TITLE}
                          cursor="pointer"
                          onClick={editNote}
                        >
                          Edit Note
                        </WsTypography>
                      </Box>
                      <Box p={1}>
                        {note.markArchived ? (
                          <WsTypography
                            variant={WsTypoVariants.SUB_TITLE}
                            cursor="pointer"
                            onClick={onUnarchive}
                          >
                            Unarchive Note
                          </WsTypography>
                        ) : (
                          <WsTypography
                            variant={WsTypoVariants.SUB_TITLE}
                            cursor="pointer"
                            onClick={onArchive}
                          >
                            Archive Note
                          </WsTypography>
                        )}
                      </Box>
                    </PopoverOnClick>
                  )}
              </Grid>
              {parent == NotesParent.NOTELISTING && noteSelectType == 'unmarked_notes' && (
                <PopoverOnClick element={<WsIconMoreVer />}>
                  <Box p={1}>
                    <WsTypography
                      variant={WsTypoVariants.SUB_TITLE}
                      cursor="pointer"
                      onClick={() => setOpenDialog(true)}
                    >
                      Add Missing Details
                    </WsTypography>
                  </Box>
                </PopoverOnClick>
              )}
            </Grid>
            <Grid item mt={2} py={1}>
              <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.TITLE} bold>
                {note?.heading}
              </WsTypography>
            </Grid>
            {!isEmpty(note?.tags) && (
              <Grid container direction="row" spacing={2}>
                <CandidateTags tags={note?.tags ?? []} initialLimit={5} isNotes={true} />
              </Grid>
            )}
            {!isAdding ? (
              <Grid item px={1}>
                <div
                  style={{ maxHeight: '500', overflow: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: note?.description }}
                />
              </Grid>
            ) : (
              <Grid item mt={2} p={1}>
                <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.BODY1}>
                  {note?.description}
                </WsTypography>
              </Grid>
            )}
            {note?.files && note.files.length > 0 && (
              <Grid item mt={4} mb={2}>
                <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.SUB_TITLE}>
                  Attachments
                </WsTypography>
                <Grid container direction="column" spacing={2} mt={1}>
                  {note.files.map((file, index) => (
                    <Grid
                      container
                      item
                      key={index}
                      direction="row"
                      alignItems="center"
                      rowSpacing={1}
                    >
                      <AiFillFileText size={20} style={{ marginRight: 4 }} color="red" />
                      <WsTypography
                        color={WsTypoColors.BLACK}
                        variant={WsTypoVariants.SUB_TITLE}
                        bold
                      >
                        {file.name}
                      </WsTypography>
                      <FileDownloadOutlinedIcon
                        sx={{
                          color: WsColors.TheBlues500,
                          cursor: 'pointer',
                          marginLeft: 1,
                          fontSize: 25,
                        }}
                        onClick={() => handleDownloadCV(file)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
          <WsDialog openDialog={openDialog} size="small" onClose={() => setOpenDialog(false)}>
            <UnmarkedMissingDetails onClose={handleClose} noteDTO={note} />
          </WsDialog>
        </Paper>
      )}
    </>
  );
};
export default NotesRightPanel;
